import { useMedplum } from '@medplum/react-hooks';
import { useEffect, useState } from 'react';
import { GEMINI_AI_DOCUMENTATION_OPT_OUT_EXT } from '@unify/fhir-model/constants';
import { loadAiToolsConsentState } from '../lib/aiToolsConsentClient';

export type GeminiAiAccess = {
  /** True when the client has no active AI consent or the provider has opted out. */
  geminiAiDisabled: boolean;
  /** True only when AI is on and the client permitted session transcription. */
  transcriptsAllowed: boolean;
};

const CLOSED: GeminiAiAccess = { geminiAiDisabled: true, transcriptsAllowed: false };

/**
 * AI documentation access for a patient + practitioner.
 *
 * The client gate is an active AI-tools Consent (a signed form). The practitioner
 * gate is still GEMINI_AI_DOCUMENTATION_OPT_OUT_EXT. Starts closed so controls do
 * not flash on before consent is known.
 */
export function useGeminiAiAccess(
  patientId: string | undefined,
  practitionerId: string | undefined,
): GeminiAiAccess {
  const medplum = useMedplum();
  const [access, setAccess] = useState<GeminiAiAccess>(CLOSED);

  useEffect(() => {
    if (!patientId && !practitionerId) {
      setAccess({ geminiAiDisabled: false, transcriptsAllowed: false });
      return;
    }
    if (!patientId) {
      setAccess(CLOSED);
      return;
    }

    let cancelled = false;
    (async () => {
      let next: GeminiAiAccess = CLOSED;
      try {
        const state = await loadAiToolsConsentState(medplum, patientId);
        const consented = state.decision === 'active';
        let providerOptedOut = false;
        if (practitionerId) {
          const pract = await medplum.readResource('Practitioner', practitionerId, { cache: 'no-cache' });
          providerOptedOut = pract.extension?.some(
            (e) => e.url === GEMINI_AI_DOCUMENTATION_OPT_OUT_EXT && e.valueBoolean === true,
          ) ?? false;
        }
        next = {
          geminiAiDisabled: !consented || providerOptedOut,
          transcriptsAllowed: consented && !providerOptedOut && state.transcription === 'permit',
        };
      } catch {
        next = CLOSED;
      }
      if (!cancelled) setAccess(next);
    })();

    return () => { cancelled = true; };
  }, [medplum, patientId, practitionerId]);

  return access;
}

/** True when LLM documentation should be hidden for this client or provider. */
export function useGeminiAiOptOut(
  patientId: string | undefined,
  practitionerId: string | undefined,
): boolean {
  return useGeminiAiAccess(patientId, practitionerId).geminiAiDisabled;
}
